import { addToast, Modal, Button } from '@octano/global-ui';
import useTexts from '../../hooks/useTexts';
import { Container, Col, Row } from 'reactstrap';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { CardTitle } from '../../../../../components/text';
import {
  ClassroomFormFields,
  ClassroomFormRequest,
} from '../../../../../types/ClassroomTypes';
import { RecordToEditBody } from '../../types';
import ClassroomForm from './../ClassroomForm';
import { getDefaultValues } from '../../utils';
import { create, update } from '../../api';

export type ClassroomFormModalMethods = {
  open: (classroom?: RecordToEditBody) => void;
  close: () => void;
};

type ClassroomFormModalProps = {
  onSave?: () => void;
};

const ClassroomFormModal = (
  { onSave }: ClassroomFormModalProps,
  ref: Ref<ClassroomFormModalMethods>,
) => {
  const texts = useTexts();

  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [classroom, setClassroom] = useState<RecordToEditBody>();

  const form = useForm<ClassroomFormFields>({
    defaultValues: getDefaultValues(),
  });

  const open = useCallback(
    (row?: RecordToEditBody) => {
      setClassroom(row);
      const defaultValues = getDefaultValues(row);
      form.reset(defaultValues);
      setVisible(true);
    },
    [form],
  );

  const close = useCallback(() => {
    setVisible(false);
    setClassroom(undefined);
    form.reset(getDefaultValues());
  }, [form]);

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }
    close();
  }, [close, saving]);

  const handleUpdate = useCallback(
    (payload: ClassroomFormRequest) => {
      if (saving || !classroom?.id) {
        return Promise.resolve();
      }
      setSaving(true);
      return update(classroom.id, payload)
        .then(({ error, status }) => {
          if (error) {
            if (status === 409) {
              return addToast({
                icon: 'error',
                color: 'danger',
                text: texts.form.conflictError,
              });
            }
            return addToast({
              icon: 'error',
              color: 'danger',
              text: texts.form.error,
            });
          }

          addToast({
            icon: 'success',
            color: 'success',
            text: texts.form.updateSuccess,
          });

          close();
          !!onSave && onSave();
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [saving, classroom?.id, close, onSave, texts],
  );

  const handleCreate = useCallback(
    (payload: ClassroomFormRequest) => {
      if (saving) {
        return Promise.resolve();
      }
      setSaving(true);
      return create(payload)
        .then(({ error, status }) => {
          if (error) {
            if (status === 409) {
              return addToast({
                icon: 'error',
                color: 'danger',
                text: texts.form.conflictError,
              });
            }
            return addToast({
              icon: 'error',
              color: 'danger',
              text: texts.form.error,
            });
          }

          addToast({
            icon: 'success',
            color: 'success',
            text: texts.form.createSuccess,
          });

          close();
          !!onSave && onSave();
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [saving, close, onSave, texts],
  );

  const handleSubmit = useCallback(
    (values: ClassroomFormFields) => {
      const payload: ClassroomFormRequest = {
        code: values.code,
        name: values.name,
        capacity: Number(values.capacity),
        typeId: values.type?.value.toString() || '',
        campusId: Number(values.campus?.value),
        id: classroom?.id,
      };

      if (classroom?.id) {
        return handleUpdate(payload);
      } else {
        return handleCreate(payload);
      }
    },
    [classroom?.id, handleUpdate, handleCreate],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const isEditionMode = !!classroom;

  return (
    <Modal isOpen={visible} size="lg" toggle={handleClose} unmountOnClose>
      <FormProvider {...form}>
        <Container>
          <Row>
            <Col xs={12}>
              <div className="text-center">
                <CardTitle
                  children={isEditionMode ? texts.edit.title : texts.form.title}
                />
              </div>
            </Col>
          </Row>
        </Container>
        <ClassroomForm />
        <Container>
          <Row className="pt-5">
            <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
              <Button
                type="button"
                outlined
                disabled={saving}
                onClick={handleClose}
                text={texts.form.cancel}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
              <Button
                type="button"
                loading={saving}
                text={texts.form.create}
                onClick={form.handleSubmit(handleSubmit)}
                fullwidth
                disabled={isEditionMode && !form.formState.isDirty}
              />
            </Col>
          </Row>
        </Container>
      </FormProvider>
    </Modal>
  );
};

export default forwardRef(ClassroomFormModal);
